import React from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/Modal';
import type { ButtonProps } from '@components/Modal/components/Footer';

type DeleteConfirmationModalProps = {
  open: boolean;
  itemText: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
};

export const DeleteConfirmationModal: React.FC<
  DeleteConfirmationModalProps
> = ({ open, itemText, onConfirm, onCancel, isDeleting = false }) => {
  const { t } = useTranslation();

  const primaryButton: ButtonProps = {
    text: isDeleting
      ? t('checklist:form.deleting')
      : t('checklist:form.delete'),
    color: 'error',
    variant: 'contained',
    onClick: onConfirm,
    disabled: isDeleting,
    loading: isDeleting
  };

  const secondaryButton: ButtonProps = {
    text: t('checklist:form.cancel'),
    variant: 'text',
    onClick: onCancel,
    disabled: isDeleting
  };

  return (
    <Modal open={open} onClose={onCancel} maxWidth="xs">
      <Modal.Header title={t('checklist:form.delete')} onClose={onCancel} />

      <Modal.Content>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('checklist:delete.confirmMessage')}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            backgroundColor: 'grey.50',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            fontStyle: 'italic',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          "{itemText}"
        </Typography>
      </Modal.Content>

      <Modal.Footer
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
      />
    </Modal>
  );
};
